import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AcceptingSlider } from "./AcceptingSlider";
import "./DashboardPage.css";

import {
  ApiError as RequestsApiError,
  listFamilyDoctorRequests,
  type RequestSourceScope,
} from "../../../../api/requests";

import {
  ApiError as PortalApiError,
  getFamilyDoctorInsights,
  getFamilyDoctorProfile,
  updateAcceptingNewPatientsStatus,
  type FamilyDoctorInsightsResponse,
} from "../../../../api/portalFamilyDoctor";

const MEET_AND_GREET_BACKLOG_LIMIT = 20;
const MEET_AND_GREET_BACKLOG_WARNING = 10;

type AttachmentFilter = "all" | "attached" | "unattached";
type TimeRangeFilter = "30" | "90" | "all" | "custom";
type RequestSourceFilter = RequestSourceScope;

function requestSourceLabel(sourceScope: RequestSourceFilter): string {
  if (sourceScope === "public") return "Public requests";
  if (sourceScope === "navigator") return "Navigator requests";
  return "All requests";
}

// UI toggle is binary per spec: Accepting / Not Accepting.
type AcceptingStatus = "accepting" | "not_accepting";

type GraphView = "unattached_only" | "all_patients";

type MetricCard = {
  label: string;
  value: number;
  hint: string;
  variant?:
    | "summary"
    | "action"
    | "warn"
    | "good"
    | "danger"
    | "muted";
};

function normalizeStatus(status: string) {
  return String(status || "").trim().toLowerCase();
}

function isAwaitingReview(status: string) {
  const s = normalizeStatus(status);
  return s === "submitted" || s === "inreview";
}

function isAccepted(status: string) {
  const s = normalizeStatus(status);
  return s === "accepted";
}

function isCancelledByPatient(status: string) {
  const s = normalizeStatus(status);
  return s === "cancelled";
}

function isWaitlisted(status: string) {
  const s = normalizeStatus(status);
  return s === "waitlist" || s === "waitlisted";
}

function isAcceptedElsewhere(status: string) {
  const s = normalizeStatus(status);
  return s === "closedacceptedelsewhere" || s === "acceptedelsewhere";
}

function isDeclined(status: string) {
  const s = normalizeStatus(status);
  return s === "declined" || s === "notaccepted";
}

function isExpired(status: string) {
  const s = normalizeStatus(status);
  return s === "expired";
}

function getRequestDate(r: any): Date | null {
  const raw =
    r?.timeline?.submittedAt ||
    r?.timeline?.createdAt ||
    r?.submittedAt ||
    r?.createdAt ||
    r?.CreatedAt ||
    r?.updatedAt ||
    r?.UpdatedAt ||
    null;

  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function utcYmd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultCustomRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - 29);

  return {
    from: utcYmd(from),
    to: utcYmd(to),
  };
}

function withinRange(
  d: Date | null,
  range: TimeRangeFilter,
  customFrom?: string,
  customTo?: string
): boolean {
  if (range === "all") return true;
  if (!d) return false;

  if (range === "custom") {
    if (!customFrom || !customTo) return false;

    const from = new Date(`${customFrom}T00:00:00.000Z`);
    const toInclusive = new Date(`${customTo}T00:00:00.000Z`);

    if (
      Number.isNaN(from.getTime()) ||
      Number.isNaN(toInclusive.getTime())
    ) {
      return false;
    }

    const toExclusive = new Date(toInclusive);
    toExclusive.setUTCDate(toExclusive.getUTCDate() + 1);

    return d >= from && d < toExclusive;
  }

  const days = range === "30" ? 30 : 90;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return d >= cutoff;
}

function normalizeBaseline(val: any): "attached" | "unattached" | null {
  const s = String(val || "").trim().toLowerCase();
  if (s === "attached") return "attached";
  if (s === "unattached") return "unattached";
  return null;
}

function parseDate(val: any): Date | null {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateMaybe(val: any): string | null {
  const d = parseDate(val);
  if (!d) return null;

  try {
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
}

function daysSinceMaybe(val: any): number | null {
  const d = parseDate(val);
  if (!d) return null;

  const ms = Date.now() - d.getTime();
  if (Number.isNaN(ms)) return null;

  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

function formatNumberMaybe(val: number | null | undefined): string {
  if (val === null || val === undefined || Number.isNaN(Number(val))) {
    return "Not set";
  }

  return new Intl.NumberFormat("en-CA", {
    maximumFractionDigits: 0,
  }).format(Number(val));
}

function formatDecimalMaybe(
  val: number | null | undefined,
  digits = 1,
  fallback = "Not enough data"
): string {
  if (val === null || val === undefined || Number.isNaN(Number(val))) {
    return fallback;
  }

  return new Intl.NumberFormat("en-CA", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(Number(val));
}

function formatRemainingCapacity(val: number | null | undefined): string {
  if (val === null || val === undefined || Number.isNaN(Number(val))) {
    return "Capacity not set";
  }

  const n = Math.max(0, Math.trunc(Number(val)));
  return `${formatNumberMaybe(n)} space${n === 1 ? "" : "s"} remaining`;
}

function formatDaysValue(val: number | null | undefined): string {
  if (val === null || val === undefined || Number.isNaN(Number(val))) {
    return "—";
  }

  const n = Number(val);
  return `${formatDecimalMaybe(n, Number.isInteger(n) ? 0 : 1)} day${
    n === 1 ? "" : "s"
  }`;
}

function formatPaceValue(val: number | null | undefined): string {
  if (val === null || val === undefined || Number.isNaN(Number(val))) {
    return "—";
  }

  return `${formatDecimalMaybe(val, 2, "0")} / day`;
}

function formatEstimatedDays(
  estimatedDays: number | null | undefined,
  status: string | null | undefined
): string {
  if (status === "capacityNotSet") return "Set capacity first";
  if (status === "capacityReached") return "Capacity reached";
  if (status === "notEnoughPace") return "Not enough data";

  if (
    estimatedDays === null ||
    estimatedDays === undefined ||
    Number.isNaN(Number(estimatedDays))
  ) {
    return "—";
  }

  const n = Math.max(0, Math.ceil(Number(estimatedDays)));
  return `${formatNumberMaybe(n)} day${n === 1 ? "" : "s"}`;
}

function monthKey(year: number, month1to12: number): string {
  const m = String(month1to12).padStart(2, "0");
  return `${year}-${m}`;
}

function monthLabel(month1to12: number): string {
  const names = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const idx = month1to12 - 1;
  return idx >= 0 && idx < 12 ? names[idx] : String(month1to12);
}

function meetAndGreetBacklogHint(count: number): string {
  if (count >= MEET_AND_GREET_BACKLOG_LIMIT) {
    return "Invitation limit reached. Clear this backlog before inviting more patients.";
  }

  if (count >= MEET_AND_GREET_BACKLOG_WARNING) {
    return `${MEET_AND_GREET_BACKLOG_LIMIT - count} invitation space${
      MEET_AND_GREET_BACKLOG_LIMIT - count === 1 ? "" : "s"
    } remaining before the limit.`;
  }

  return "Invited for Meet & Greet but not yet attached or closed.";
}

function meetAndGreetBacklogVariant(count: number): MetricCard["variant"] {
  if (count >= MEET_AND_GREET_BACKLOG_LIMIT) return "danger";
  if (count >= MEET_AND_GREET_BACKLOG_WARNING) return "warn";
  return "warn";
}

export default function DashboardPage() {
  const initialCustomRange = defaultCustomRange();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [requests, setRequests] = useState<any[]>([]);
  const [insights, setInsights] =
    useState<FamilyDoctorInsightsResponse | null>(null);

  const [attachmentFilter, setAttachmentFilter] =
    useState<AttachmentFilter>("all");
  const [timeRange, setTimeRange] = useState<TimeRangeFilter>("all");
  const [customFrom, setCustomFrom] = useState(initialCustomRange.from);
  const [customTo, setCustomTo] = useState(initialCustomRange.to);
  const [requestSourceScope, setRequestSourceScope] =
    useState<RequestSourceFilter>("all");

  const [graphView, setGraphView] = useState<GraphView>("unattached_only");
  const [graphYear, setGraphYear] = useState<number>(new Date().getFullYear());

  const [acceptingStatus, setAcceptingStatus] =
    useState<AcceptingStatus>("not_accepting");
  const [acceptingBusy, setAcceptingBusy] = useState(false);
  const [acceptingStatusUpdatedAt, setAcceptingStatusUpdatedAt] = useState<
    string | null
  >(null);

  const OFF_PCT = 10;
  const ON_PCT = 90;

  async function load() {
    setLoading(true);
    setError(null);

    try {
      const [reqs, profile, insightsResponse] = await Promise.all([
        listFamilyDoctorRequests({
          status: "All",
          sourceScope: requestSourceScope,
        }),
        getFamilyDoctorProfile(),
        getFamilyDoctorInsights({
          sourceScope: requestSourceScope,
        }),
      ]);

      setRequests(Array.isArray(reqs) ? reqs : []);
      setInsights(insightsResponse || null);

      const doctor = profile?.doctor || {};
      const accepting = !!doctor?.acceptingNewPatients;

      setAcceptingStatus(accepting ? "accepting" : "not_accepting");
      setAcceptingStatusUpdatedAt(
        doctor?.acceptingStatusUpdatedAt
          ? String(doctor.acceptingStatusUpdatedAt)
          : null
      );
    } catch (e: any) {
      const msg =
        e instanceof RequestsApiError || e instanceof PortalApiError
          ? e.message
          : "Failed to load dashboard";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestSourceScope]);

  const filteredRequests = useMemo(() => {
    const base = (requests || []).filter((r) =>
      withinRange(getRequestDate(r), timeRange, customFrom, customTo)
    );

    if (attachmentFilter === "all") return base;

    const wanted = attachmentFilter === "attached" ? "attached" : "unattached";

    return base.filter(
      (r) => normalizeBaseline(r?.patientStatusBeforeRequest) === wanted
    );
  }, [
    requests,
    attachmentFilter,
    timeRange,
    customFrom,
    customTo,
  ]);

  const selectedRequestSourceLabel = requestSourceLabel(requestSourceScope);

  const metrics = useMemo(() => {
    const awaiting = filteredRequests.filter((r) => isAwaitingReview(r.status))
      .length;

    const accepted = filteredRequests.filter((r) => isAccepted(r.status)).length;

    const waitlisted = filteredRequests.filter((r) => isWaitlisted(r.status))
      .length;

    const attachmentComplete = (requests || []).filter((r) => {
      const completedAt = parseDate(r?.timeline?.attachmentCompletedAt);
      if (!completedAt) return false;
      if (
        !withinRange(
          completedAt,
          timeRange,
          customFrom,
          customTo
        )
      ) {
        return false;
      }

      if (attachmentFilter === "all") return true;

      const wanted =
        attachmentFilter === "attached" ? "attached" : "unattached";

      return normalizeBaseline(r?.patientStatusBeforeRequest) === wanted;
    }).length;

    const declined = filteredRequests.filter((r) => isDeclined(r.status)).length;

    const elsewhere = filteredRequests.filter((r) =>
      isAcceptedElsewhere(r.status)
    ).length;

    const cancelled = filteredRequests.filter((r) =>
      isCancelledByPatient(r.status)
    ).length;

    const expired = filteredRequests.filter((r) => isExpired(r.status)).length;

    const total = filteredRequests.length;

    return {
      awaiting,
      accepted,
      waitlisted,
      attachmentComplete,
      declined,
      elsewhere,
      cancelled,
      expired,
      total,
    };
  }, [
    filteredRequests,
    requests,
    attachmentFilter,
    timeRange,
    customFrom,
    customTo,
  ]);

  const metricCards = useMemo<MetricCard[]>(() => {
    return [
      {
        label: "Total requests",
        value: metrics.total,
        hint: `${selectedRequestSourceLabel} matching selected filters`,
        variant: "summary",
      },
      {
        label: "Awaiting review",
        value: metrics.awaiting,
        hint: "New requests needing action",
        variant: "action",
      },
      {
        label: "Meet & Greet backlog",
        value: metrics.accepted,
        hint: meetAndGreetBacklogHint(metrics.accepted),
        variant: meetAndGreetBacklogVariant(metrics.accepted),
      },
      {
        label: "Waitlisted",
        value: metrics.waitlisted,
        hint: "Patients currently on your waitlist",
        variant: "muted",
      },
      {
        label: "Attachments completed",
        value: metrics.attachmentComplete,
        hint: "Completed attachments in range",
        variant: "good",
      },
      {
        label: "Declined by you",
        value: metrics.declined,
        hint: "Declined by you",
        variant: "danger",
      },
      {
        label: "Cancelled by patient",
        value: metrics.cancelled,
        hint: "Patient cancelled request",
        variant: "muted",
      },
      {
        label: "Auto-expired",
        value: metrics.expired,
        hint: "Expired automatically",
        variant: "muted",
      },
      {
        label: "Accepted elsewhere",
        value: metrics.elsewhere,
        hint: "Patient attached elsewhere",
        variant: "muted",
      },
    ];
  }, [metrics, selectedRequestSourceLabel]);

  const availableGraphYears = useMemo(() => {
    const dates = (requests || [])
      .map((r) => parseDate(r?.timeline?.attachmentCompletedAt))
      .filter(Boolean) as Date[];

    const currentYear = new Date().getFullYear();

    if (dates.length === 0) {
      return [currentYear];
    }

    let minYear = currentYear;
    for (const d of dates) {
      const y = d.getFullYear();
      if (y < minYear) minYear = y;
    }

    const years: number[] = [];
    for (let y = minYear; y <= currentYear; y++) years.push(y);
    return years;
  }, [requests]);

  useEffect(() => {
    if (!availableGraphYears.length) return;
    if (!availableGraphYears.includes(graphYear)) {
      setGraphYear(availableGraphYears[availableGraphYears.length - 1]);
    }
  }, [availableGraphYears, graphYear]);

  const graphSeries = useMemo(() => {
    const all = requests || [];

    const completed = all.filter(
      (r) => !!parseDate(r?.timeline?.attachmentCompletedAt)
    );

    const baselineFiltered =
      graphView === "unattached_only"
        ? completed.filter(
            (r) =>
              normalizeBaseline(r?.patientStatusBeforeRequest) === "unattached"
          )
        : completed;

    const counts = new Map<string, number>();
    for (const r of baselineFiltered) {
      const d = parseDate(r?.timeline?.attachmentCompletedAt);
      if (!d) continue;
      if (d.getFullYear() !== graphYear) continue;

      const k = monthKey(graphYear, d.getMonth() + 1);
      counts.set(k, (counts.get(k) || 0) + 1);
    }

    const series = [];
    for (let m = 1; m <= 12; m++) {
      const k = monthKey(graphYear, m);
      series.push({
        key: k,
        label: monthLabel(m),
        count: counts.get(k) || 0,
      });
    }

    return series;
  }, [requests, graphView, graphYear]);

  const graphMax = useMemo(() => {
    let max = 0;
    for (const p of graphSeries) {
      if (p.count > max) max = p.count;
    }
    return max;
  }, [graphSeries]);

  const acceptingUpdatedLabel = useMemo(
    () => formatDateMaybe(acceptingStatusUpdatedAt),
    [acceptingStatusUpdatedAt]
  );

  const acceptingConfirmationWarning = useMemo(() => {
    if (acceptingStatus !== "accepting") return null;

    const daysSince = daysSinceMaybe(acceptingStatusUpdatedAt);

    if (daysSince === null) {
      return "Please confirm your accepting status to stay listed as accepting new patients.";
    }

    if (daysSince >= 30) {
      return "This status is overdue for confirmation. Please re-confirm to stay listed as accepting new patients.";
    }

    if (daysSince >= 29) {
      return "Please re-confirm today. If not confirmed, this may automatically change to Not accepting.";
    }

    if (daysSince >= 23) {
      const daysLeft = 30 - daysSince;
      return `Please re-confirm soon to stay listed as accepting new patients (${daysLeft} day${
        daysLeft === 1 ? "" : "s"
      } left).`;
    }

    return null;
  }, [acceptingStatus, acceptingStatusUpdatedAt]);

  const capacity = insights?.capacity || null;
  const throughput = insights?.throughput || null;
  const forecast = insights?.forecast || null;

  const capacityUpdatedLabel = useMemo(
    () => formatDateMaybe(capacity?.remainingCapacityUpdatedAt),
    [capacity?.remainingCapacityUpdatedAt]
  );

  const remainingCapacityLabel = useMemo(
    () => formatRemainingCapacity(capacity?.remainingCapacity),
    [capacity?.remainingCapacity]
  );

  const averageTimeToAttachmentLabel = useMemo(
    () => formatDaysValue(throughput?.avgDaysToAttachment),
    [throughput?.avgDaysToAttachment]
  );

  const paceLabel = useMemo(
    () => formatPaceValue(throughput?.pacePerDay),
    [throughput?.pacePerDay]
  );

  const estimatedDaysToFullCapacityLabel = useMemo(
    () =>
      formatEstimatedDays(
        forecast?.estimatedDaysToFullCapacity,
        forecast?.forecastStatus
      ),
    [forecast?.estimatedDaysToFullCapacity, forecast?.forecastStatus]
  );

  const capacityRunwayMessage = useMemo(() => {
    if (!capacity || capacity.capacitySource === "missing") {
      return "Add your intake capacity in Profile to enable this forecast.";
    }

    if (forecast?.forecastMessage) {
      return forecast.forecastMessage;
    }

    if ((capacity.remainingCapacity ?? 0) <= 0) {
      return "Your current capacity is filled based on the latest completed attachments and capacity settings.";
    }

    if (!throughput?.pacePerDay || throughput.pacePerDay <= 0) {
      return "Not enough completed attachments in the last 30 days to estimate when capacity may be filled.";
    }

    return "This private planning view updates as attachments are completed.";
  }, [capacity, forecast?.forecastMessage, throughput?.pacePerDay]);

  const capacityFootnote = useMemo(() => {
    if (!capacity || capacity.capacitySource === "missing") {
      return "Capacity is private and is not shown to patients.";
    }

    if (capacity.capacitySource === "override") {
      return capacityUpdatedLabel
        ? `Manual remaining-capacity override active · Updated ${capacityUpdatedLabel}`
        : "Manual remaining-capacity override active";
    }

    if (capacityUpdatedLabel) {
      return `Calculated from declared capacity and completed attachments · Updated ${capacityUpdatedLabel}`;
    }

    return "Calculated from declared capacity and completed attachments.";
  }, [capacity, capacityUpdatedLabel]);

  const capacityProgressPct = useMemo(() => {
    const declared = capacity?.declaredCapacity;
    const used = capacity?.usedCapacity;

    if (
      declared === null ||
      declared === undefined ||
      used === null ||
      used === undefined ||
      Number.isNaN(Number(declared)) ||
      Number.isNaN(Number(used)) ||
      Number(declared) <= 0
    ) {
      return null;
    }

    return Math.max(0, Math.min(100, (Number(used) / Number(declared)) * 100));
  }, [capacity?.declaredCapacity, capacity?.usedCapacity]);

  async function commitFromPct(pct: number) {
    const next: AcceptingStatus = pct >= 60 ? "accepting" : "not_accepting";
    if (next === acceptingStatus) return;

    const prev = acceptingStatus;

    setAcceptingStatus(next);
    setAcceptingBusy(true);
    setError(null);

    try {
      const response = await updateAcceptingNewPatientsStatus(
        next === "accepting"
      );

      setAcceptingStatus(
        response.acceptingNewPatients ? "accepting" : "not_accepting"
      );
      setAcceptingStatusUpdatedAt(
        response.acceptingStatusUpdatedAt ||
          response.heartbeatAt ||
          new Date().toISOString()
      );
    } catch (e: any) {
      setAcceptingStatus(prev);
      const msg =
        e instanceof PortalApiError
          ? e.message
          : "Failed to update accepting status";
      setError(msg);
    } finally {
      setAcceptingBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="ocf-dashboard">
        <div className="ocf-dashboard-header">
          <div>
            <h1 className="ocf-dashboard-title">Dashboard</h1>
            <p className="ocf-dashboard-subtitle">
              Overview of your patient requests and intake activity.
            </p>
          </div>
        </div>

        <div className="ocf-dashboard-state">
          <div className="ocf-muted">Loading…</div>
        </div>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="ocf-dashboard">
        <div className="ocf-dashboard-header">
          <div>
            <h1 className="ocf-dashboard-title">Dashboard</h1>
            <p className="ocf-dashboard-subtitle">
              Overview of your patient requests and intake activity.
            </p>
          </div>

          <div className="ocf-dashboard-actions">
            <button className="ocf-btn ocf-btn--secondary" onClick={load}>
              Refresh
            </button>
          </div>
        </div>

        <div className="ocf-error">{error}</div>
      </div>
    );
  }

  const awaitingCount = metrics.awaiting;
  const meetAndGreetBacklogCount = metrics.accepted;
  const isMeetAndGreetBacklogBlocked =
    meetAndGreetBacklogCount >= MEET_AND_GREET_BACKLOG_LIMIT;

  return (
    <div className="ocf-dashboard">
      <div className="ocf-dashboard-header">
        <div>
          <h1 className="ocf-dashboard-title">Dashboard</h1>
          <p className="ocf-dashboard-subtitle">
            Overview of your patient requests and intake activity.
          </p>
        </div>

        <div className="ocf-dashboard-actions">
          <button
            className="ocf-btn ocf-btn--secondary"
            onClick={load}
            disabled={loading || acceptingBusy}
          >
            Refresh
          </button>
        </div>
      </div>

      {isMeetAndGreetBacklogBlocked ? (
        <div className="ocf-today-callout" role="alert" aria-live="polite">
          <div className="ocf-today-callout-copy">
            <strong>
              Meet &amp; Greet invitation limit reached (
              {meetAndGreetBacklogCount}/{MEET_AND_GREET_BACKLOG_LIMIT}).
            </strong>
            <span className="ocf-today-callout-hint">
              Clear your existing backlog before inviting more patients.
            </span>
          </div>
          <Link
            to={
              requestSourceScope === "navigator"
                ? "/portal/family-doctor/navigator-requests"
                : "/portal/family-doctor/requests"
            }
            className="ocf-today-callout-cta"
          >
            Clear backlog →
          </Link>
        </div>
      ) : null}

      {awaitingCount > 0 ? (
        <div className="ocf-today-callout" role="status" aria-live="polite">
          <div className="ocf-today-callout-copy">
            <strong>
              You have {awaitingCount} new patient request
              {awaitingCount === 1 ? "" : "s"} to review.
            </strong>
            <span className="ocf-today-callout-hint">
              Invite for Meet &amp; Greet, waitlist, or decline from the queue.
            </span>
          </div>
          <Link
            to={
              requestSourceScope === "navigator"
                ? "/portal/family-doctor/navigator-requests"
                : "/portal/family-doctor/requests"
            }
            className="ocf-today-callout-cta"
          >
            Go to queue →
          </Link>
        </div>
      ) : null}

      <section className="ocf-section ocf-section--hero">
        <div className="ocf-card ocf-accepting-status">
          <div className="ocf-accepting-row">
            <div className="ocf-accepting-copy">
              <div className="ocf-accepting-eyebrow">Accepting status</div>
              <p className="ocf-accepting-description">
                This is shown on your public profile. Confirm at least once
                every 30 days. If not confirmed, this may automatically change
                to Not accepting.
              </p>

              {acceptingConfirmationWarning ? (
                <div className="ocf-accepting-warning" role="note">
                  <strong>Action needed:</strong>{" "}
                  {acceptingConfirmationWarning}
                </div>
              ) : null}

              <div className="ocf-accepting-meta">
                {acceptingUpdatedLabel ? (
                  <div className="ocf-accepting-meta-item">
                    Last confirmed: <strong>{acceptingUpdatedLabel}</strong>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="ocf-accepting-control">
              <AcceptingSlider
                value={acceptingStatus === "accepting"}
                disabled={acceptingBusy}
                onCommit={(next) => commitFromPct(next ? ON_PCT : OFF_PCT)}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="ocf-section ocf-capacity-runway-section">
        <div className="ocf-capacity-runway">
          <div className="ocf-capacity-runway-main">
            <div className="ocf-capacity-runway-head">
              <div className="ocf-capacity-runway-eyebrow">
                Remaining capacity
              </div>

              <Link
                to="/portal/family-doctor/profile"
                className="ocf-btn ocf-btn--secondary"
              >
                Update capacity
              </Link>
            </div>

            <div className="ocf-capacity-runway-number">
              {remainingCapacityLabel}
            </div>

            <p className="ocf-capacity-runway-message">
              {capacityRunwayMessage}
            </p>

            {capacityProgressPct !== null ? (
              <div
                className="ocf-capacity-runway-progress"
                aria-label="Capacity progress"
              >
                <span
                  style={{
                    width: `${capacityProgressPct}%`,
                  }}
                />
              </div>
            ) : null}

            <div className="ocf-capacity-runway-footnote">
              {capacityFootnote}
            </div>
          </div>
        </div>
      </section>

      <section className="ocf-section">
        <div className="ocf-card ocf-section-card ocf-perf-section">
          <h2 className="ocf-section-title">Performance summary</h2>
          <p className="ocf-chart-subtitle ocf-perf-subtitle">
            Based on performance in the last 30 days only.
          </p>

          <div
            className="ocf-perf-grid"
            aria-label="30-day performance summary"
          >
            <div className="ocf-perf-card">
              <div className="ocf-perf-label">Average time to attachment</div>
              <div className="ocf-perf-value">
                {averageTimeToAttachmentLabel}
              </div>
              <div className="ocf-perf-hint">
                Submitted → attachment completed
              </div>
            </div>

            <div className="ocf-perf-card">
              <div className="ocf-perf-label">Attachment pace</div>
              <div className="ocf-perf-value">{paceLabel}</div>
              <div className="ocf-perf-hint">
                Average patients attached per day
              </div>
            </div>

            <div className="ocf-perf-card">
              <div className="ocf-perf-label">
                Estimated days to full capacity
              </div>
              <div className="ocf-perf-value">
                {estimatedDaysToFullCapacityLabel}
              </div>
              <div className="ocf-perf-hint">
                Based on remaining capacity and pace
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ocf-section">
        <div className="ocf-card ocf-filters-card">
          <h2 className="ocf-section-title">Filters</h2>
          <div className="ocf-dashboard-filters">
            <label className="ocf-filter">
              <span className="ocf-filter-label">Request source</span>
              <select
                className="ocf-select"
                value={requestSourceScope}
                onChange={(e) =>
                  setRequestSourceScope(e.target.value as RequestSourceFilter)
                }
                disabled={loading}
              >
                <option value="all">All requests</option>
                <option value="public">Public requests</option>
                <option value="navigator">Navigator requests</option>
              </select>
            </label>

            <label className="ocf-filter">
              <span className="ocf-filter-label">Time range</span>
              <select
                className="ocf-select"
                value={timeRange}
                onChange={(e) =>
                  setTimeRange(e.target.value as TimeRangeFilter)
                }
                disabled={loading}
              >
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="all">All time</option>
                <option value="custom">Custom</option>
              </select>
            </label>

            <label className="ocf-filter">
              <span className="ocf-filter-label">Baseline status</span>
              <select
                className="ocf-select"
                value={attachmentFilter}
                onChange={(e) =>
                  setAttachmentFilter(e.target.value as AttachmentFilter)
                }
                disabled={loading}
              >
                <option value="all">All patients</option>
                <option value="unattached">Unattached only</option>
                <option value="attached">Attached</option>
              </select>
            </label>
          </div>

          {timeRange === "custom" ? (
            <div className="ocf-dashboard-filters">
              <label className="ocf-filter">
                <span className="ocf-filter-label">From</span>
                <input
                  type="date"
                  className="ocf-select"
                  value={customFrom}
                  max={customTo}
                  onChange={(e) => {
                    if (e.target.value) {
                      setCustomFrom(e.target.value);
                    }
                  }}
                  disabled={loading}
                />
              </label>

              <label className="ocf-filter">
                <span className="ocf-filter-label">To</span>
                <input
                  type="date"
                  className="ocf-select"
                  value={customTo}
                  min={customFrom}
                  max={utcYmd(new Date())}
                  onChange={(e) => {
                    if (e.target.value) {
                      setCustomTo(e.target.value);
                    }
                  }}
                  disabled={loading}
                />
              </label>
            </div>
          ) : null}

          <div className="ocf-muted ocf-filters-hint">
            Metrics below reflect the selected request source, time range, and
            baseline status.
          </div>
        </div>
      </section>

      <section className="ocf-section">
        <div className="ocf-card ocf-section-card">
          <h2 className="ocf-section-title">Patient Requests Overview</h2>

          <div className="ocf-dashboard-kpis">
            {metricCards.map((card) => (
              <div
                key={card.label}
                className={`ocf-card ocf-kpi${
                  card.variant ? ` ocf-kpi--${card.variant}` : ""
                }`}
              >
                <div className="ocf-kpi-label">{card.label}</div>
                <div className="ocf-big-number">{card.value}</div>
                <div className="ocf-muted">{card.hint}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ocf-section">
        <div className="ocf-card ocf-section-card">
          <div className="ocf-chart-head">
            <div>
              <h2 className="ocf-chart-title">
                Attachments Completed Over Time
              </h2>
              <p className="ocf-chart-subtitle">
                Based on the date each attachment was completed. Shows all 12
                months in the selected year, including months with zero activity.
              </p>
            </div>

            <div className="ocf-chart-filters">
              <label className="ocf-filter ocf-filter-compact">
                <span className="ocf-filter-label">Year</span>
                <select
                  className="ocf-select"
                  value={graphYear}
                  onChange={(e) => setGraphYear(parseInt(e.target.value, 10))}
                  disabled={loading}
                >
                  {availableGraphYears.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>

              <label className="ocf-filter ocf-filter-compact">
                <span className="ocf-filter-label">View</span>
                <select
                  className="ocf-select"
                  value={graphView}
                  onChange={(e) => setGraphView(e.target.value as GraphView)}
                  disabled={loading}
                >
                  <option value="unattached_only">Unattached only</option>
                  <option value="all_patients">All patients</option>
                </select>
              </label>
            </div>
          </div>

          <div className="ocf-graph-chart">
            <div className="ocf-graph-grid">
              {graphSeries.map((p) => {
                const h =
                  graphMax > 0
                    ? Math.max(4, Math.round((p.count / graphMax) * 140))
                    : 4;

                return (
                  <div
                    key={p.key}
                    className="ocf-graph-col"
                    title={`${p.label} ${graphYear}: ${p.count}`}
                  >
                    <div className="ocf-graph-barwrap">
                      {p.count > 0 ? (
                        <div className="ocf-graph-count">{p.count}</div>
                      ) : null}
                      <div
                        className={`ocf-graph-bar${
                          p.count === 0 ? " ocf-graph-bar--empty" : ""
                        }`}
                        style={{ height: p.count > 0 ? h : 2 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="ocf-graph-months">
              {graphSeries.map((p) => (
                <span key={p.key} className="ocf-graph-label ocf-muted">
                  {p.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}